// Schedule › To schedule › a sold estimate's deposits (BL-006), Blueprint's (BL-189): the deposits taken,
// or that none is, and "Take a deposit…" (a Link) for those who record payments, which opens a FormSheet:
// cash, cheque or e-transfer (payments.record with estimateId), the amount suggested by
// estimates.depositPercent, never more than what's left of the signed total. As Riverstone's
// EstimateDeposits (job/Deposits.tsx), which the job sheet keeps.
import { useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { Link, SelectField, TextField } from '@flowology/ui-web/fl';
import { FormSheet } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { newId } from '../shared/ids.ts';
import {
  DEPOSIT_METHODS,
  DEPOSIT_NOTE,
  depositAmount,
  depositText,
  heldCents,
  suggestedCents,
  type DepositMethod,
} from '../job/deposits-model.ts';
import { useDeposits } from '../job/use-deposits.ts';

const say = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'That didn’t go through. Try again.';
const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

export function SoldDeposits({
  estimateId,
  estimateNumber,
  customerName,
  totalCents,
  mayTake,
  onTaken,
}: {
  estimateId: string;
  estimateNumber: string;
  customerName: string;
  totalCents: number;
  mayTake: boolean;
  onTaken: (said: string) => void;
}) {
  const [version, setVersion] = useState(0);
  const deposits = useDeposits({ estimateId }, version);
  const [taking, setTaking] = useState(false);
  if (deposits === null) return null;
  const held = heldCents(deposits);
  const left = Math.max(0, totalCents - held);
  return (
    <div className="bp-tobook-deposits" role="group" aria-label={`Deposits on estimate ${estimateNumber}`}>
      {deposits.length > 0 ? (
        <ul className="bp-tobook-deposit-lines">
          {deposits.map((d) => (
            <li key={d.creditId}>{depositText(d)}</li>
          ))}
        </ul>
      ) : (
        <span className="bp-tobook-sub">No deposit taken.</span>
      )}
      {mayTake && left > 0 && (
        <Link
          onPress={() => setTaking(true)}
          label={`Take a deposit on estimate ${estimateNumber} for ${customerName}`}
        >
          Take a deposit…
        </Link>
      )}
      <TakeDeposit
        isOpen={taking}
        estimateId={estimateId}
        estimateNumber={estimateNumber}
        customerName={customerName}
        totalCents={totalCents}
        held={held}
        onClose={() => setTaking(false)}
        onTaken={(said) => {
          setVersion((v) => v + 1);
          onTaken(said);
        }}
      />
    </div>
  );
}

function TakeDeposit({
  isOpen,
  estimateId,
  estimateNumber,
  customerName,
  totalCents,
  held,
  onClose,
  onTaken,
}: {
  isOpen: boolean;
  estimateId: string;
  estimateNumber: string;
  customerName: string;
  totalCents: number;
  held: number;
  onClose: () => void;
  onTaken: (said: string) => void;
}) {
  const left = Math.max(0, totalCents - held);
  const [text, setText] = useState('');
  const [method, setMethod] = useState<DepositMethod>('cheque');
  const [reference, setReference] = useState('');
  // one payment per attempt: a lost answer retried records it once
  const paymentId = useRef(newId());
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setText('');
      setMethod('cheque');
      setReference('');
      paymentId.current = newId();
    }
  }
  useEffect(() => {
    if (!isOpen) return;
    client
      .call<{ value: number }>('settings.get', { key: 'estimates.depositPercent' })
      .then((r) => {
        const s = suggestedCents(totalCents, Number(r.value) || 0, held);
        if (s > 0) setText((t) => t || (s / 100).toFixed(2));
      })
      .catch(() => undefined);
  }, [isOpen, totalCents, held]);
  const amount = depositAmount(text, left);
  return (
    <FormSheet
      isOpen={isOpen}
      onClose={onClose}
      title={`A deposit from ${customerName}, estimate ${estimateNumber}`}
      line={`${cad(left)} of the signed ${cad(totalCents)} is left for deposits. ${DEPOSIT_NOTE}`}
      submitLabel={'cents' in amount ? `Take ${cad(amount.cents)}` : 'Take the deposit'}
      isDirty={method !== 'cheque' || !!reference.trim()}
      isSubmitDisabled={!('cents' in amount)}
      onSubmit={async () => {
        if (!('cents' in amount)) return;
        try {
          await client.call('payments.record', {
            paymentId: paymentId.current,
            method,
            amountCents: amount.cents,
            receivedAt: new Date().toISOString(),
            estimateId,
            ...(reference.trim() ? { reference: reference.trim() } : {}),
          });
        } catch (e) {
          throw new Error(say(e), { cause: e });
        }
        onTaken(`Deposit of ${cad(amount.cents)} taken for estimate ${estimateNumber}.`);
      }}
    >
      <TextField
        label="Amount"
        inputMode="decimal"
        value={text}
        onChange={setText}
        error={text.trim() && 'problem' in amount ? amount.problem : null}
      />
      <SelectField
        label="How they paid"
        value={method}
        onChange={(v) => setMethod(v as DepositMethod)}
        options={DEPOSIT_METHODS}
      />
      {method !== 'cash' && (
        <TextField
          label={method === 'cheque' ? 'Cheque number (optional)' : 'Reference (optional)'}
          maxLength={100}
          value={reference}
          onChange={setReference}
        />
      )}
    </FormSheet>
  );
}
